/**
 * AnimatedPath Component
 * SVG path with smooth flowing animation using stroke-dasharray
 */

import { motion } from 'framer-motion';
import type { CloudProvider } from './ProviderNode';
import { providerConfig } from './ProviderNode';

interface AnimatedPathProps {
  id: string;
  d: string;
  provider: CloudProvider;
  isActive: boolean;
  animationSpeed?: 'slow' | 'normal' | 'fast';
}

const speedConfig = {
  slow: 1.2,
  normal: 0.8,
  fast: 0.5,
};

export function AnimatedPath({
  id,
  d,
  provider,
  isActive,
  animationSpeed = 'normal',
}: AnimatedPathProps) {
  const config = providerConfig[provider];
  const duration = speedConfig[animationSpeed];

  return (
    <g>
      {/* Base path - always visible, muted when inactive */}
      <path
        id={`${id}-base`}
        d={d}
        fill="none"
        stroke="hsl(var(--muted))"
        strokeWidth="2"
        strokeLinecap="round"
        opacity={isActive ? 0 : 0.4}
      />

      {/* Active path with flowing animation */}
      {isActive && (
        <motion.path
          id={id}
          d={d}
          fill="none"
          stroke={config.color}
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray="12 8"
          filter={`drop-shadow(0 0 6px ${config.glowColor})`}
          initial={{ strokeDashoffset: 0 }}
          animate={{ strokeDashoffset: -20 }}
          transition={{
            duration,
            ease: 'linear',
            repeat: Infinity,
          }}
        />
      )}
    </g>
  );
}
